import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { InfoIconWithTooltip } from "../../../components/UIToolkit/molecules/InfoIconWithTooltip";
import { STATUS } from "../../../theme/tokens";

type Props = {
	/** Distinguishes the badges rendered across the Staffing-Hours surfaces (grid cells + summary stat). */
	dataTestId: string;
	/**
	 * Optional leading text (a date or shift window). When provided the badge renders in the grid-cell
	 * layout — an ellipsis-truncated label followed by the marker. When omitted only the marker renders,
	 * so inline callers (e.g. the summary stat) can compose their own leading value.
	 */
	label?: string;
};

/**
 * Informational Daylight-Saving marker: a clock glyph + tooltip flagging an employee-day/shift that
 * crossed a DST transition. The hours are already correct (real elapsed time) — this only tells a
 * reviewer why the day reads as 23h/25h. Single source for the glyph, tone token and tooltip copy so
 * the three Staffing-Hours surfaces (table date cell, expand window cell, summary stat) never drift.
 */
export const DstBadge: FC<Props> = ({ dataTestId, label }) => {
	const { t } = useTranslation();
	const marker = (
		<InfoIconWithTooltip
			title={t("pbj_model.staffing.dst.tooltip")}
			icon={AccessTimeRoundedIcon}
			iconSx={{ fontSize: "16px", color: STATUS.INFO.main }}
			dataTestId={dataTestId}
		/>
	);
	if (label === undefined) return marker;
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
			<Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
				{label}
			</Box>
			{marker}
		</Box>
	);
};
